import type { Dispatch, SetStateAction } from 'react';

export function formatFileSize(bytes: number): string {
  if (bytes < 1000) {
    return `${bytes} B`;
  } else if (bytes < 1000 * 1000) {
    return `${(bytes / 1000).toFixed(2)} KB`;
  } else if (bytes < 1000 * 1000 * 1000) {
    return `${(bytes / (1000 * 1000)).toFixed(2)} MB`;
  } else {
    return `${(bytes / (1000 * 1000 * 1000)).toFixed(2)} GB`;
  }
}

export function countWords(str: string): number {
  return str
    .trim()
    .split(/\s+/)
    .filter(word => word.length > 0).length;
}

export function getFileColor(type: string) {
  switch (type) {
    case 'pdf':
      return 'bg-red-700 text-white';
    case 'msword':
      return 'bg-blue-700 text-white';
    case 'docx':
      return 'bg-blue-700 text-white';
    case 'doc':
      return 'bg-blue-700 text-white';
    default:
      return 'bg-gray-50 text-dark';
  }
}

export function getFileName(fileName: string) {
  return fileName.split('/')[fileName.split('/').length - 1];
}

export const setStateAsync = <T>(
  setState: Dispatch<SetStateAction<T>>,
  updater: SetStateAction<T>
): Promise<void> => {
  return new Promise(resolve => {
    setState(prevState => {
      // If updater is a function (like normal setState usage), call it
      const nextState =
        typeof updater === 'function'
          ? (updater as (prev: T) => T)(prevState)
          : updater;
      resolve(); // resolves immediately after React processes the update
      return nextState;
    });
  });
};

export function getPasswordStrength(password: string) {
  if (!password) return 'Weak';

  const hasNumber = /\d/.test(password);
  const hasSpecialChar = /[^A-Za-z0-9]/.test(password);
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const lengthValid = password.length >= 8;

  // Strong → meets all rules and includes upper & lower case mix
  if (
    lengthValid &&
    hasNumber &&
    hasSpecialChar &&
    hasUpperCase &&
    hasLowerCase
  ) {
    return 'Strong';
  }

  // Medium → meets your base rules (number + special + length)
  if (lengthValid && hasNumber && hasSpecialChar) {
    return 'Medium';
  }

  // Weak otherwise
  return 'Weak';
}

export function formatDate(dateString: string, format?: string): string {
  // Handle null, undefined, or empty date strings gracefully
  if (!dateString) {
    return '';
  }

  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    // Only log in development if it's actually a malformed date, not just empty
    if (process.env.NODE_ENV === 'development') {
      console.debug(`Invalid date input: ${dateString}`);
    }
    return '';
  }

  // Set default format to include time if none is provided, or keep it as is
  const formatString = format || 'MMM DD, YYYY';

  const YYYY = date.getFullYear().toString();
  const YY = YYYY.slice(-2);

  const monthIndex = date.getMonth();
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const MMMM = monthNames[monthIndex];
  const MMM = MMMM.slice(0, 3);
  const M = monthIndex + 1;
  const MM = M.toString().padStart(2, '0');

  const D = date.getDate();
  const DD = D.toString().padStart(2, '0');

  const hours24 = date.getHours();
  const H = hours24;
  const HH = H.toString().padStart(2, '0');

  const minutes = date.getMinutes();
  const m = minutes;
  const mm = m.toString().padStart(2, '0');

  const seconds = date.getSeconds();
  const s = seconds;
  const ss = s.toString().padStart(2, '0');

  // 12-hour format and AM/PM
  const hours12 = hours24 % 12 || 12;
  const h = hours12;
  const hh = h.toString().padStart(2, '0');
  const A = hours24 < 12 ? 'AM' : 'PM';

  const tokens: Record<string, string> = {
    YYYY: YYYY,
    YY: YY,
    MMMM: MMMM,
    MMM: MMM,
    MM: MM,
    M: M.toString(),
    DD: DD,
    D: D.toString(),
    HH: HH,
    H: H.toString(),
    hh: hh,
    h: h.toString(),
    mm: mm,
    m: m.toString(),
    ss: ss,
    s: s.toString(),
    A: A,
  };

  let formattedDate = formatString;

  const tokenKeys = Object.keys(tokens).sort((a, b) => b.length - a.length);

  tokenKeys.forEach(token => {
    formattedDate = formattedDate.replace(
      new RegExp(`\\b${token}\\b`, 'g'),
      tokens[token]
    );
  });

  return formattedDate;
}

export const adjustEditTextareaHeight = (
  current: HTMLTextAreaElement,
  MIN_ROWS: number,
  MAX_ROWS: number
) => {
  const textarea = current;

  // Reset height to get accurate scrollHeight measurement
  // This ensures the textarea can shrink when content is removed
  textarea.style.height = '0px';

  // Get the computed line height and padding
  const computedStyle = window.getComputedStyle(textarea);
  const lineHeight = parseFloat(computedStyle.lineHeight);
  const paddingTop = parseFloat(computedStyle.paddingTop);
  const paddingBottom = parseFloat(computedStyle.paddingBottom);

  // Calculate the actual line height (fallback if not set)
  const actualLineHeight =
    lineHeight || parseFloat(computedStyle.fontSize) * 1.2;

  // Calculate min and max heights based on rows
  const minHeight = actualLineHeight * MIN_ROWS + paddingTop + paddingBottom;
  const maxHeight = actualLineHeight * MAX_ROWS + paddingTop + paddingBottom;

  // Get the scrollHeight (actual content height)
  const scrollHeight = textarea.scrollHeight;

  // Apply min and max bounds, then set the height directly
  const newHeight = Math.max(minHeight, Math.min(maxHeight, scrollHeight));
  textarea.style.height = `${newHeight}px`;
};

/**
 * Validates that a string contains meaningful content
 * Returns true if the string has at least one alphanumeric character
 * Returns false if the string is empty, only whitespace, or only symbols
 */
export const isValidContent = (value: string): boolean => {
  if (!value || typeof value !== 'string') {
    return false;
  }

  // Trim whitespace and check if empty
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return false;
  }

  // Check if string contains at least one alphanumeric character (letter or number)
  // This regex matches any letter (including unicode) or digit
  const hasAlphanumeric = /[\p{L}\p{N}]/u.test(trimmed);

  return hasAlphanumeric;
};

/**
 * Clean up markdown and anonymization artifacts from profile summary.
 * Handles edge cases like:
 * - `-Focused` (dash without space)
 * - `text **` (orphaned asterisks)
 * - Malformed markdown that doesn't have matching pairs
 * - Raw JSON strings stored as profile summary
 */
export const cleanProfileSummary = (summary: string): string => {
  if (!summary) return '';

  // CRITICAL FIX: Handle raw JSON stored as profile summary
  // Some profiles have summary like: {"profile_type": "Founder/Entrepreneur", "industry": "Healthcare/Biotech",...}
  const trimmed = summary.trim();
  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    try {
      const parsed = JSON.parse(trimmed);
      // Try to extract meaningful text from common JSON fields
      const extractedParts: string[] = [];

      // Common fields that contain useful summary text
      const textFields = ['summary', 'bio', 'description', 'profile_summary', 'about', 'overview'];
      for (const field of textFields) {
        if (parsed[field] && typeof parsed[field] === 'string') {
          extractedParts.push(parsed[field]);
        }
      }

      // If no text fields, build a summary from structured data
      if (extractedParts.length === 0) {
        if (parsed.profile_type) extractedParts.push(parsed.profile_type);
        if (parsed.industry) extractedParts.push(`in ${parsed.industry}`);
        if (parsed.stage) extractedParts.push(`at ${parsed.stage} stage`);
        if (parsed.focus) extractedParts.push(`focused on ${parsed.focus}`);
        if (parsed.objectives && Array.isArray(parsed.objectives)) {
          extractedParts.push(`looking for ${parsed.objectives.join(', ')}`);
        }
      }

      if (extractedParts.length > 0) {
        // Clean the extracted text (recursive call with non-JSON string)
        return cleanProfileSummary(extractedParts.join('. '));
      }
      // If we couldn't extract anything useful, fall through to regex cleaning
    } catch {
      // Not valid JSON, continue with normal cleaning
    }
  }

  return summary
    // Remove escaped quotes at start/end
    .replace(/^["']|["']$/g, '')
    // Remove markdown headers with various formats (## Focus, ## Profile Essence, etc.)
    .replace(/#{1,6}\s*[A-Za-z ]+\n?/g, '')
    // Also handle inline markdown headers
    .replace(/^#+\s*/gm, '')
    // Remove bold markers (paired)
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    // Remove italic markers (paired)
    .replace(/\*([^*]+)\*/g, '$1')
    // Remove underline bold (paired)
    .replace(/__([^_]+)__/g, '$1')
    // Remove underline italic (paired)
    .replace(/_([^_]+)_/g, '$1')
    // Remove orphaned asterisks (unpaired ** or * at start/end/standalone)
    .replace(/\*+/g, '')
    // Remove orphaned underscores (unpaired __ or _ at word boundaries)
    .replace(/(?:^|[\s])_+|_+(?:[\s]|$)/g, ' ')
    // Replace [Name] placeholders with generic text
    .replace(/\[Name\]/g, 'Professional')
    .replace(/\[.*?\]/g, '') // Remove other bracketed placeholders
    // Convert literal \n to actual line breaks, then to spaces
    .replace(/\\n/g, ' ')
    // Remove markdown list markers (with or without space after)
    .replace(/^[-*•]\s*/gm, '')
    .replace(/^\d+[.)]\s*/gm, '') // Numbered lists with . or )
    // Handle leading dash at start of text (like "-Focused")
    .replace(/^-(?=\S)/gm, '')
    // Remove markdown code blocks
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`([^`]+)`/g, '$1')
    // Clean up any remaining markdown artifacts
    .replace(/\|\s*/g, ', ')
    // Remove multiple spaces and newlines
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};